import { iconForGroupedFilter } from 'module/helpers/filter/entity-for-filter-type';
import {
  asFilterableFieldOption,
  asGroupedFilterableFieldOption,
  getFilterGroupType,
} from 'module/helpers/filter/grouped-field-transformer';
import { useTranslation } from 'module/hooks/use-translation';
import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { GroupedOptions } from 'module/types/filter-field/field/grouped-field';
import {
  AiFieldFilter,
  AiFieldFilterType,
} from 'module/types/filter-field/filter';
import { GroupCriteria } from 'module/types/filter-field/grouped-filter/filter';
import { AiRichTextEditor } from 'module/ui/components/atoms/ai/rich-text-editor';
import { Button } from 'module/ui/components/atoms/button';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import {
  GroupedDropdown,
  GroupedDropdownTriggerProps,
  GroupIndicatorIcon,
  GroupedDropdownTrigger,
  TextWithEllipsis,
} from 'module/ui/components/molecules/grouped-dropdown';
import { Short01 } from 'module/ui/components/styles';
import { faChevronDown, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Form as AntForm } from 'antd';
import { flatten } from 'lodash';
import { FC, useCallback, useMemo, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { criterion } from './criterion';
import { getDefaultCriteria } from './get-default-criteria';
import { BaseFilterComponentProps } from '../types';

export const RemoveButton = styled(Button).attrs({
  type: 'text',
  size: 'small',
  icon: <FontAwesomeIcon icon={faXmark} />,
})`
  ${FontAwesomeIcon} {
    color: var(--icon-secondary);
  }

  ${({ onClick }) =>
    onClick != null
      ? css`
          visibility: visible;
        `
      : css`
          visibility: hidden;
        `};
`;
const Label = styled.div`
  min-width: 40px;
  ${Short01};
  opacity: 0.5;
  text-align: right;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  height: 32px;
  min-height: 32px;
  padding: 4px 0;
`;

const Form = styled(AntForm)`
  .ant-form-item {
    margin-right: var(--spacing-03);
  }
  .ant-form-item:last-child {
    margin-right: 0;
  }

  .ant-form-item-control-input {
    height: 100%;
  }
`;

const TextWithIconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  max-width: 190px;
`;
const DropdownIndicator = styled(FontAwesomeIcon)`
  color: var(--icon-secondary);
`;

const TriggerComponent = ({ value, onClick }: GroupedDropdownTriggerProps) => {
  const defaultFallback = useTranslation('select_with_ellipsis');
  const filterGroupType = value && getFilterGroupType(value?.sourceType);

  return (
    <GroupedDropdownTrigger data-cy="select.fields" onClick={onClick}>
      <TextWithIconWrapper>
        {filterGroupType ? (
          <GroupIndicatorIcon>
            {iconForGroupedFilter[filterGroupType]}
          </GroupIndicatorIcon>
        ) : null}
        <TextWithEllipsis>{value?.label || defaultFallback}</TextWithEllipsis>
      </TextWithIconWrapper>
      <DropdownIndicator icon={faChevronDown} />
    </GroupedDropdownTrigger>
  );
};

interface Props extends BaseFilterComponentProps {
  hideRemoveButton?: boolean;
  hideMatchTypeText?: boolean;
  matchTypeLabel: string | React.ReactNode;
  fields: GroupedOptions<FilterableField>[];
  filter?: GroupCriteria;
  onChange: (filter: GroupCriteria) => void;
  onRemove?: () => void;
}

const StyledForm = styled(Form)`
  width: 100%;
  display: inline-flex;
  align-items: flex-start;
  flex-wrap: nowrap;
`;

const StyledFormItem = styled(Form.Item)`
  flex: 1;
  margin-bottom: 0 !important;
  display: flex;
  align-items: flex-start;

  .ant-form-item-control {
    flex: 1;
    width: 100%;
    min-width: 0;
  }

  .ant-row {
    flex: 1;
  }

  .ant-form-item-control-input {
    flex: 1;
    width: 100%;
    min-width: 0;
  }

  .ant-form-item-control-input-content {
    width: 100%;
    display: flex;
    min-width: 0;
  }
`;

export const GroupedCriteria: FC<Props> = ({
  hideRemoveButton = false,
  hideMatchTypeText = false,
  fields,
  matchTypeLabel,
  filter,
  onChange,
  onRemove,
  getFilterOperationTypeOptions,
  getSelectEditorComponents,
  getOperationOptionRenderer,
  userOptionsGetter,
}) => {
  const fieldAsOptions = useMemo(
    () => asGroupedFilterableFieldOption(fields),
    [fields],
  );
  const allFields = useMemo(
    () => flatten(fields.map((x) => x.options)),
    [fields],
  );

  const onFieldChange = useCallback(
    (selected: FilterableField) => {
      const selectedField = allFields.find(
        (f) => f.fieldId === selected.fieldId,
      )!;

      onChange({
        id: getFilterGroupType(selected.sourceType),
        criteria: getDefaultCriteria(selectedField),
      });
    },
    [allFields, onChange],
  );

  const isInitialized = useRef(false);

  useEffect(() => {
    isInitialized.current = true;
  }, []);

  return (
    <StyledForm layout="inline">
      {hideRemoveButton ? null : (
        <Form.Item>
          <RemoveButton onClick={onRemove} />
        </Form.Item>
      )}
      {hideMatchTypeText ? null : (
        <Form.Item>
          <Label>{matchTypeLabel}</Label>
        </Form.Item>
      )}
      {filter?.criteria?.type !== AiFieldFilterType.Ai ? (
        <>
          <Form.Item>
            <GroupedDropdown
              triggerComponent={TriggerComponent}
              title={matchTypeLabel}
              value={
                filter != null
                  ? asFilterableFieldOption(filter.criteria.field)
                  : filter
              }
              options={fieldAsOptions}
              onChange={onFieldChange}
            />
          </Form.Item>
          {criterion({
            filter: filter?.criteria,
            onChange: (fieldFilter) =>
              onChange({
                id: getFilterGroupType(fieldFilter.field.sourceType),
                criteria: fieldFilter,
              }),
            getFilterOperationTypeOptions,
            getSelectEditorComponents,
            getOperationOptionRenderer,
            userOptionsGetter,
          })}
        </>
      ) : (
        <>
          <StyledFormItem>
            <AiRichTextEditor
              value={{
                content: (filter?.criteria as any)?.value || '',
                editedByUser: !(filter?.criteria as AiFieldFilter)
                  ?.isAiGenerated,
              }}
              onChange={(newValue) => {
                if (isInitialized.current) {
                  onChange({
                    ...filter!,
                    criteria: {
                      ...filter!.criteria,
                      value: newValue.content,
                      isAiGenerated: false,
                    } as AiFieldFilter,
                  });
                }
              }}
            />
          </StyledFormItem>
        </>
      )}
    </StyledForm>
  );
};
